/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components imports
import { Grid, Layout, Menu, Image, Space, Avatar } from "antd";
import { DollarOutlined, DownOutlined, UserOutlined, LogoutOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import Logo from "@/assets/logo_dark.svg";

// misc
import { NAV_PROJECTS, NAV_LOGIN, NAV_EXAMPLES, NAV_PROFILE, NAV_SUBSCRIPTIONS } from "@/constants";
import { useUserInfo } from "@/api/user";
import { getInitialsFromUserData } from "@/utils/string/getInitialsFromUserData";
import { queryClient } from "@/utils/queryClient.ts";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { LanguageSelector } from "../UI/LanguageSelector";
import { useWorkspacesList } from "@/api/workspaces";
import { RAG_DOCUMENTATION_URL } from "@/constants";
import { tokens as Tokens } from "@/utils/tokens";
import { ITokens } from "@/interfaces/User.interface";

const { Header } = Layout;
const baseClassName = "global-header";
const { useBreakpoint } = Grid;

/**
 * The `HeaderBar` component represents the application header with navigation links.
 * @returns {JSX.Element} The rendered `HeaderBar` component.
 */
export const HeaderBar = (): JSX.Element => {
  const screens = useBreakpoint();
  const tokensInstance = Tokens;
  const isScreenBelow = (size: "sm" | "md" | "lg" | "xl" | "xxl") => screens.xs || (screens.sm && !screens[size]);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const userAuth:ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  const { data: currentUserData } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);

  const { data: workspacesList } = useWorkspacesList({
    enabled: !!accessToken,
  });
  const [workspaceId, setWorkspaceId] = useState<string | null>(workspacesList ? workspacesList[0]?.uuid : null);

  const [current, setCurrent] = useState(location.pathname === "/" || location.pathname === "" ? "/dashboard" : location.pathname);
  const [isProfileAvatarActive, setIsProfileAvatarActive] = useState(false);

  const logout = useCallback(() => {
    queryClient.clear();
    navigate(NAV_LOGIN);
  }, [navigate]);

  const userMenuItemsArray = useMemo(() => {
    return [
      {
        icon: <UserOutlined />,
        label: (
          <NavLink
            to={replaceUrlParams(NAV_PROFILE, {
              workspaceId: workspaceId as string,
            })}
            className={({ isActive, isPending }) => (isPending ? "pending" : isActive ? "active" : "")}
          >
            {t("menu.profile")}
          </NavLink>
        ),
        key: NAV_PROFILE,
      },
      {
        icon: <DollarOutlined />,
        label: (
          <NavLink
            to={`${replaceUrlParams(NAV_SUBSCRIPTIONS, {
              workspaceId: workspaceId as string,
            })}?tab=subscriptions-list`}
            className={({ isActive, isPending }) => (isPending ? "pending" : isActive ? "active" : "")}
          >
            {t("menu.subscriptions")}
          </NavLink>
        ),
        key: NAV_SUBSCRIPTIONS,
      },
      {
        icon: <LogoutOutlined />,
        label: <a onClick={logout}>{t("menu.logout")}</a>,
        key: "logout",
      },
    ];
  }, [logout, t, workspaceId]);

  //
  // USE EFFECT
  //
  useEffect(() => {
    if (!location) {
      return;
    }

    // Check if the current path includes any of the user menu items' paths
    const isProfileActive = userMenuItemsArray.some((item) => location.pathname.includes(item.key));

    // Set isProfileAvatarActive based on the check
    setIsProfileAvatarActive(isProfileActive);

  }, [location, current, userMenuItemsArray]);

  useEffect(() => {
    setWorkspaceId(workspacesList ? workspacesList[0]?.uuid : null);
  }, [workspacesList]);

  const handleOnMenuSelect: MenuProps["onSelect"] = ({ keyPath }) => {
    setIsProfileAvatarActive(keyPath.includes(`${NAV_PROFILE}_wrapper`));
  };

  const handleClick: MenuProps["onClick"] = (e: any) => {
    if (e.key === RAG_DOCUMENTATION_URL || e.key === NAV_EXAMPLES) {
      return;
    }
    setCurrent(e.key);
  };

  const renderUserMenu = () => {
    return userMenuItemsArray;
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: `${NAV_PROFILE}_wrapper`,
      label: (
        <Space size={10}>
          <Avatar shape="square" className={`${isProfileAvatarActive && "active"} ${baseClassName}__profile-avatar`}>
            {getInitialsFromUserData(currentUserData) || <UserOutlined className="fs-lg" />}
          </Avatar>
          <DownOutlined className={`${isScreenBelow("md") ? "d-none" : ""} v-align-middle`} style={{ fontSize: "16px" }} />
        </Space>
      ),
      children: renderUserMenu(),
      popupClassName: `${baseClassName}__user-avatar__profile-menu`,
      className: `${baseClassName}__user-avatar`,
    },
  ];

  const items: MenuProps["items"] = [
    {
      label: (
        <NavLink
          to={replaceUrlParams(NAV_PROJECTS, {
            workspaceId: workspaceId as string,
          })}
          className={({ isActive, isPending }) => (isPending ? "pending" : isActive ? "active" : "")}
        >
          {t("menu.projects")}
        </NavLink>
      ),
      key: replaceUrlParams(NAV_PROJECTS, {
        workspaceId: workspaceId as string,
      }),
    },
    {
      label: (
        <Link to={RAG_DOCUMENTATION_URL} target="_blank" rel="noopener noreferrer">
          {t("menu.documentation")}
        </Link>
      ),
      key: RAG_DOCUMENTATION_URL,
    },
    {
      label: (
        <Link to={"https://github.com/Helvia/rag-buddy"} target="_blank" rel="noopener noreferrer">
          {t("menu.examples")}
        </Link>
      ),
      key: NAV_EXAMPLES,
    },
  ];

  /**
   * Handles the menu item click event.
   * @param {MenuProps['onClick']} e - The click event.
   */
  return (
    <Header className={`${baseClassName}`}>
      <div>
        <Link
          to={replaceUrlParams(NAV_PROJECTS, {
            workspaceId: workspaceId as string,
          })}
          className="mb-0"
        >
          <Image width={55} src={Logo} preview={false} alt="RAG-logo" />
        </Link>
        {accessToken ? (
          <>
            <Menu
              theme="light"
              className="flex-grow-1 justify-content-center"
              onSelect={handleOnMenuSelect}
              onClick={handleClick}
              selectedKeys={[current]}
              triggerSubMenuAction="click"
              mode="horizontal"
              items={items}
            />
            <Menu
              theme="light"
              className="no-background-color-menu"
              onSelect={handleOnMenuSelect}
              onClick={handleClick}
              selectedKeys={[]}
              triggerSubMenuAction="click"
              mode="horizontal"
              items={userMenuItems}
            />
          </>
        ) : (
          <div className="float-right">
            <LanguageSelector />
          </div>
        )}
      </div>
    </Header>
  );
};
